/**
 * Correlation tab: the lead and lag indicator traces over 180 days on two
 * scales with the day-120 marker and the lead-time caption, the insight
 * boxes (lead indicator, lead time, coefficient) and the historical
 * accuracy table.
 */
import { useCallback, useMemo } from 'react';
import uPlot from 'uplot';
import { cn } from '@/lib/cn';
import { accuracyTable, correlationInfo, correlationSeries } from '../../adapters/analysis';
import { UPlotChart } from '../../components/UPlotChart';
import { axisDefaults, useChartColours } from '../../components/chartTheme';
import { Card, SectionTitle } from '../../components/layout';
import { fmtDate } from '../../lib/format';

export function CorrelationView({ dataId, subsystemId }: { dataId: string; subsystemId: string }) {
  const info = useMemo(() => correlationInfo(dataId, subsystemId), [dataId, subsystemId]);
  const series = useMemo(() => correlationSeries(dataId, subsystemId), [dataId, subsystemId]);
  const table = useMemo(() => accuracyTable(dataId, subsystemId), [dataId, subsystemId]);
  const colours = useChartColours();

  const data = useMemo<uPlot.AlignedData>(() => [Array.from({ length: series.days }, (_, i) => i), series.lead, series.lag], [series]);

  const buildOptions = useCallback(
    (): Omit<uPlot.Options, 'width' | 'height'> => {
      const c = colours;
      const draw = (u: uPlot) => {
        const r = uPlot.pxRatio || window.devicePixelRatio || 1;
        const x = u.valToPos(series.markerDay, 'x', true);
        const { top, height } = u.bbox;
        const ctx = u.ctx;
        ctx.save();
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.lineWidth = r;
        ctx.setLineDash([5 * r, 5 * r]);
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, top + height);
        ctx.stroke();
        ctx.restore();
      };
      return {
        legend: { show: false },
        cursor: { show: true, x: true, y: false, drag: { x: false, y: false } },
        scales: { x: { time: false }, lead: { auto: false, range: [30, 60] }, lag: { auto: false, range: [0, 100] } },
        axes: [
          { ...axisDefaults(c), values: (_u: uPlot, splits: number[]) => splits.map((v) => `d${v}`), space: 50 },
          { ...axisDefaults(c), scale: 'lag', size: 40, values: (_u: uPlot, splits: number[]) => splits.map((v) => (v === 0 ? 'Low' : v === 100 ? 'High' : '')) },
        ],
        series: [
          {},
          { label: info.leadIndicator, scale: 'lead', stroke: c.warning, width: 2, points: { show: false } },
          { label: info.lagIndicator, scale: 'lag', stroke: c.nominal, width: 2.5, points: { show: false } },
        ],
        hooks: { draw: [draw] },
      };
    },
    [colours, series.markerDay, info],
  );

  return (
    <div className="flex flex-col gap-3">
      <div>
        <SectionTitle>Lead and lag indicators</SectionTitle>
        <div className="text-[11px] text-ink-2">
          {info.leadIndicator} changes precede failures by {info.leadTime}.
        </div>
        <UPlotChart data={data} buildOptions={buildOptions} height={160} className="mt-1" />
        <div className="flex items-center justify-between px-1 text-[10px] text-ink-3">
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-3 bg-warning" /> {info.leadIndicator}
          </span>
          <span>{info.leadTime} lead time, marker at day {series.markerDay}</span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-0.5 w-3 bg-nominal" /> {info.lagIndicator}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Card title="Lead indicator">
          <div className="text-ink">{info.leadIndicator}</div>
        </Card>
        <Card title="Lead time">
          <div className="font-mono text-ink">{info.leadTime}</div>
        </Card>
        <Card title="Coefficient">
          <div className="font-mono text-ink">{info.coefficient.toFixed(2)}</div>
        </Card>
      </div>
      <p className="text-[11px] leading-snug text-ink-3">{info.description}</p>
      <div>
        <SectionTitle>Historical accuracy</SectionTitle>
        <table className="w-full text-[11px]">
          <thead className="text-left text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="py-1 font-medium">Event</th>
              <th className="py-1 font-medium">Predicted</th>
              <th className="py-1 font-medium">Actual</th>
              <th className="py-1 text-right font-medium">Variance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {table.map((row) => (
              <tr key={row.event}>
                <td className="py-1 text-ink">{row.event}</td>
                <td className="py-1 font-mono text-ink-2">{fmtDate(row.predictedMs)}</td>
                <td className="py-1 font-mono text-ink-2">{fmtDate(row.actualMs)}</td>
                <td className={cn('py-1 text-right font-mono', row.varianceDays >= 0 ? 'text-caution' : 'text-nominal')}>
                  {row.varianceDays >= 0 ? '+' : ''}
                  {row.varianceDays} d
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-1 text-[10px] text-ink-3">Seeded reference events (SIM); real accuracy tracking needs the maintenance record.</p>
      </div>
    </div>
  );
}
